import IconRefresh from "@synthesis/shared-icons/IconRefresh";
import { IconArrowLeft, IconArrowRight, IconCheckmark, IconCloseCircleFill, IconFileFill } from "@synthesis/shared-icons";
import { useSubmitRequest } from "@synthesis/shared-services/external/application/hooks";
import { get2QueryKey, useNotifyPaid } from "@synthesis/shared-services/external/payment/hooks";
import { useTransition } from "@synthesis/shared-services/internal/application/hooks";
import { Button, setPromiseSnack } from "@synthesis/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import type { ComponentProps, ReactNode } from "react";

import { ImportApplicationButton } from "../../../components/import-application";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import { invalidateExternalApplicationQueries, invalidateInternalApplicationQueries } from "../../../shared/utils/invalidate-application-queries";
import type { ApplicationDetail } from "../application-detail-page.lib";
import { usePageStore } from "../application-detail-page.lib";
import { ApplicationDetailExportButton } from "./application-detail-export-button";
import { buildControlPopupAction, TRANSITION_SNACK_MESSAGES } from "./application-control-popup.lib";
import {
  getApplicationControlDefinitions,
  isTransitionAction,
  type ApplicationControlButtonVariant,
  type ApplicationControlDefinition,
  type ApplicationUiAction,
  type TransitionAction,
} from "./application-detail-controls.lib";
import { observer } from "mobx-react-lite";

type ApplicationControlsProps = {
  application: ApplicationDetail;
  className?: string;
};

const VARIANT_BUTTON_CLASS: Record<Exclude<ApplicationControlButtonVariant, "take-work">, string> = {
  success: "border-system-success-90 text-system-success-50 border-2",
  attention: "border-system-attention-90 text-system-attention-50 border-2",
  error: "border-system-error-90 text-system-error-50 border-2",
  info: "border-system-info-90 text-system-info-50 border-2",
};

const getActionIcon = (action: ApplicationUiAction, variant: ApplicationControlButtonVariant): ReactNode => {
  if (variant === "take-work") {
    return <IconFileFill className="size-5" aria-hidden />;
  }

  if (action === "ntp-approve" || action === "ng-approve" || action === "start-decision") {
    return <IconCheckmark className="text-system-success-50 size-5" aria-hidden />;
  }

  if (variant === "attention") {
    return <IconArrowLeft className="text-system-attention-50 size-5" aria-hidden />;
  }

  if (variant === "error") {
    return <IconCloseCircleFill className="text-system-error-50 size-5" aria-hidden />;
  }

  if (variant === "info") {
    return <IconRefresh className="text-system-info-50 size-5" aria-hidden />;
  }

  return <IconArrowRight className="text-system-success-50 size-5" aria-hidden />;
};

const ApplicationActionButton = ({
  control,
  application,
  onOpenPopup,
}: {
  control: Extract<ApplicationControlDefinition, { type: "action" }>;
  application: ApplicationDetail;
  onOpenPopup: (action: ApplicationUiAction) => void;
}) => {
  const queryClient = useQueryClient();
  const transitionMutation = useTransition();
  const { action, label, variant, requiresModal } = control;

  const runTransition = (transitionAction: TransitionAction) => {
    if (!application.requestId) {
      return;
    }

    const snackMessages = TRANSITION_SNACK_MESSAGES[transitionAction];
    const promise = transitionMutation
      .mutateAsync({ path: { requestId: application.requestId, action: transitionAction }, body: undefined })
      .then(() => invalidateInternalApplicationQueries(queryClient, application.requestId));

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: snackMessages,
      },
    });
  };

  const handleClick = () => {
    if (requiresModal || !isTransitionAction(action)) {
      onOpenPopup(action);
      return;
    }

    runTransition(action);
  };

  const buttonProps: ComponentProps<typeof Button> = {
    type: "button",
    size: "sm",
    disabled: transitionMutation.isPending || !application.requestId || control.disabled,
    onClick: handleClick,
    children: label,
    leftIcon: getActionIcon(action, variant),
  };

  if (variant === "take-work") {
    return <Button {...buttonProps} theme="primary" />;
  }

  return <Button {...buttonProps} theme="secondary" className={VARIANT_BUTTON_CLASS[variant]} />;
};

const NOTIFY_PAYMENT_SNACK_MESSAGES = {
  pending: "Отправка уведомления...",
  fulfilled: "Уведомление об оплате отправлено",
  rejected: "Не удалось отправить уведомление. Попробуйте еще раз",
} as const;

const ExternalApplicantNotifyPaymentButton = ({ requestId }: { requestId: string }) => {
  const queryClient = useQueryClient();
  const notifyPaidMutation = useNotifyPaid();

  const handleClick = () => {
    if (!requestId) {
      return;
    }

    const promise = (async () => {
      const updatedView = await notifyPaidMutation.mutateAsync({ path: { id: requestId } });
      queryClient.setQueryData(get2QueryKey({ path: { id: requestId } }), updatedView);
      await invalidateExternalApplicationQueries(queryClient, requestId);
    })();

    setPromiseSnack({ promise, promiseSettings: { title: NOTIFY_PAYMENT_SNACK_MESSAGES } });
  };

  return (
    <Button
      type="button"
      theme="secondary"
      size="sm"
      leftIcon={<IconArrowRight className="text-system-success-50 size-5" aria-hidden />}
      className="border-system-success-90 text-system-success-50 border-2"
      disabled={notifyPaidMutation.isPending || !requestId}
      onClick={handleClick}
    >
      Уведомить о платеже
    </Button>
  );
};

const ExternalApplicantActionButton = ({ application }: { application: ApplicationDetail }) => {
  const queryClient = useQueryClient();
  const submitMutation = useSubmitRequest();

  const handleClick = () => {
    if (!application.requestId) {
      return;
    }

    const promise = submitMutation
      .mutateAsync({ path: { id: application.requestId } })
      .then(() => invalidateExternalApplicationQueries(queryClient, application.requestId));

    setPromiseSnack({
      promise,
      promiseSettings: {
        title: {
          pending: "Отправка уведомления",
          fulfilled: "Заявка направлена в РЖД",
          rejected: "Не удалось отправить уведомление",
        },
      },
    });
  };

  return (
    <Button
      type="button"
      theme="secondary"
      size="sm"
      leftIcon={<IconArrowRight className="text-system-success-50 size-5" aria-hidden />}
      className="border-system-success-90 text-system-success-50 border-2"
      disabled={submitMutation.isPending || !application.requestId}
      onClick={handleClick}
    >
      Внесены корректировки, уведомить РЖД
    </Button>
  );
};

export const ApplicationControls = observer(({ application, className }: ApplicationControlsProps) => {
  const pageStore = usePageStore();
  const clientType = useClientType();
  const roles = useUserRoles();
  const controls = getApplicationControlDefinitions(application, clientType, roles, pageStore.paymentCanApprove);

  const handleOpenPopup = (action: ApplicationUiAction) => {
    if (!application.requestId) {
      return;
    }

    const popupAction = buildControlPopupAction(action, application.requestId);

    if (popupAction) {
      pageStore?.setPopupAction(popupAction);
    }
  };

  if (controls.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-3">
        {controls.map((control) => {
          const key = control.type === "action" ? control.action : control.type;

          switch (control.type) {
            case "export":
              return <ApplicationDetailExportButton key={key} application={application} disabled={control.disabled} />;
            case "import":
              return (
                <ImportApplicationButton
                  key={key}
                  disabled={control.disabled}
                  onOpen={() => pageStore?.setPopupAction({ type: "import-application" })}
                />
              );
            case "submit-revision":
              return <ExternalApplicantActionButton key={key} application={application} />;
            case "notify-payment":
              return <ExternalApplicantNotifyPaymentButton key={key} requestId={application.requestId} />;
            case "action":
              return <ApplicationActionButton key={key} control={control} application={application} onOpenPopup={handleOpenPopup} />;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
});
